import { useEffect, useId, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";

import ninja from "./ninja.svg";
import S from "./SneakingNinja.module.scss";

interface Position {
  left: number;
  top: number;
}

interface Hideout {
  element: HTMLElement;
  side: "left" | "right" | "top";
  offset: number;
  concealOnArrival?: boolean;
}

const getHideoutElements = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      "main button, main a, main input, main article, main section, main div, main [data-ninja-hideout]"
    )
  ).filter(element => {
    const bounds = element.getBoundingClientRect();
    if (bounds.width < 32 || bounds.height < 24 || !element.getClientRects().length) return false;
    const style = getComputedStyle(element);
    if (style.visibility !== "visible" || Number(style.opacity) === 0) return false;
    if (element.tagName !== "DIV" || element.matches(".p-card, .p-panel, [data-ninja-hideout]"))
      return true;
    const hasBackground =
      style.backgroundColor !== "rgba(0, 0, 0, 0)" || style.backgroundImage !== "none";
    const hasBorder = [
      style.borderTopWidth,
      style.borderRightWidth,
      style.borderBottomWidth,
      style.borderLeftWidth,
    ].some(width => parseFloat(width) > 0);
    return (
      bounds.width >= 128 &&
      bounds.height >= 64 &&
      (hasBackground || hasBorder || style.boxShadow !== "none")
    );
  });

const getHideoutPosition = (hideout: Hideout): Position => {
  const bounds = hideout.element.getBoundingClientRect();
  const isVerticalEdge = hideout.side === "left" || hideout.side === "right";
  return {
    left: isVerticalEdge
      ? hideout.side === "left"
        ? bounds.left - 40
        : bounds.right - 24
      : bounds.left + Math.min(hideout.offset, Math.max(0, bounds.width - 64)),
    top: isVerticalEdge
      ? bounds.top + Math.min(hideout.offset, Math.max(0, bounds.height - 64))
      : bounds.top - 32,
  };
};

const getVisiblePanels = () => {
  const panels = getHideoutElements().filter(element => {
    const bounds = element.getBoundingClientRect();
    return (
      !element.matches("button, a, input") &&
      bounds.width >= 128 &&
      bounds.height >= 64 &&
      bounds.top >= 40 &&
      bounds.top < window.innerHeight - 64 &&
      bounds.left >= 0 &&
      bounds.right <= window.innerWidth
    );
  });
  return panels
    .filter(panel => !panels.some(other => other !== panel && panel.contains(other)))
    .sort((first, second) => {
      const firstBounds = first.getBoundingClientRect();
      const secondBounds = second.getBoundingClientRect();
      return firstBounds.top - secondBounds.top || firstBounds.left - secondBounds.left;
    });
};

const getConcealedPose = (side: Hideout["side"]) => ({
  transform:
    side === "top"
      ? "translateY(36px)"
      : side === "left"
        ? "translateX(44px)"
        : "translateX(-44px)",
  clipPath:
    side === "top"
      ? "inset(-8px -8px 64px -8px)"
      : side === "left"
        ? "inset(-8px 64px -8px -8px)"
        : "inset(-8px -8px -8px 64px)",
});

const animatePeek = (element: HTMLElement, side: Hideout["side"]) =>
  element.animate(
    [
      getConcealedPose(side),
      { transform: "translate(0, 0)", clipPath: getComputedStyle(element).clipPath },
    ],
    { duration: 1800, easing: "ease-out" }
  );

export default function SneakingNinja({
  hidden,
  onDismiss,
}: {
  hidden: boolean;
  onDismiss: () => void;
}) {
  const menu = useRef<Menu>(null);
  const companion = useRef<HTMLElement>(null);
  const traveler = useRef<HTMLDivElement>(null);
  const placementArea = useRef<HTMLButtonElement>(null);
  const pickupOrigin = useRef<Position | null>(null);
  const previousHideout = useRef<Hideout | null>(null);
  const movement = useRef<Animation | null>(null);
  const entrance = useRef(false);
  const entranceTimer = useRef<number | undefined>(undefined);
  const coverTimer = useRef<number | undefined>(undefined);
  const panelTrail = useRef<HTMLElement[]>([]);
  const menuId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickedUp, setPickedUp] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [hideout, setHideout] = useState<Hideout | null>(null);
  const [idle, setIdle] = useState(false);
  const [pendingHideout, setPendingHideout] = useState<Hideout | null>(null);
  const [concealed, setConcealed] = useState(false);

  useEffect(() => {
    if (hidden) return;
    let timer: number | undefined;
    const reset = (event?: Event) => {
      const target = event?.target;
      if (
        target instanceof Node &&
        (companion.current?.contains(target) || document.getElementById(menuId)?.contains(target))
      )
        return;
      if (
        companion.current &&
        (event?.type === "pointermove" || (event instanceof KeyboardEvent && event.key === "Tab"))
      )
        return;
      window.clearTimeout(timer);
      window.clearTimeout(entranceTimer.current);
      window.clearTimeout(coverTimer.current);
      entrance.current = false;
      setIdle(false);
      setConcealed(false);
      setMenuOpen(false);
      setPickedUp(false);
      setPendingHideout(null);
      setHideout(null);
      setPosition(null);
      if (!document.hidden)
        timer = window.setTimeout(() => {
          const first = getVisiblePanels()[0];
          if (!first) return;
          const source: Hideout = { element: first, side: "top", offset: 24 };
          panelTrail.current = [];
          entrance.current = true;
          setPosition(getHideoutPosition(source));
          setHideout(source);
          setIdle(true);
        }, 30_000);
    };
    const events = ["pointermove", "pointerdown", "keydown", "wheel", "scroll", "visibilitychange"];
    events.forEach(event =>
      document.addEventListener(event, reset, { capture: true, passive: true })
    );
    reset();
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(entranceTimer.current);
      window.clearTimeout(coverTimer.current);
      events.forEach(event => document.removeEventListener(event, reset, true));
    };
  }, [hidden, menuId]);

  useEffect(() => {
    if (!pendingHideout || !idle || hidden) return;
    const timer = window.setTimeout(() => {
      setHideout(pendingHideout);
      setPendingHideout(null);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [pendingHideout, idle, hidden]);

  useEffect(() => {
    if (!hideout || !idle || hidden || menuOpen || pickedUp || pendingHideout) return;
    if (panelTrail.current.at(-1) !== hideout.element) {
      panelTrail.current = [
        ...panelTrail.current.filter(panel => panel.isConnected && panel !== hideout.element),
        hideout.element,
      ];
    }
    let lastPosition: Position | null = null;
    const scheduleSneak = () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      entranceTimer.current = window.setTimeout(() => {
        const origin = getHideoutPosition(hideout);
        const next = getVisiblePanels()
          .filter(
            panel =>
              panel !== hideout.element &&
              panel !== panelTrail.current.at(-2) &&
              !panel.contains(hideout.element) &&
              !hideout.element.contains(panel)
          )
          .sort((first, second) => {
            const firstPosition = getHideoutPosition({ element: first, side: "top", offset: 24 });
            const secondPosition = getHideoutPosition({ element: second, side: "top", offset: 24 });
            return (
              panelTrail.current.indexOf(first) - panelTrail.current.indexOf(second) ||
              Math.hypot(firstPosition.left - origin.left, firstPosition.top - origin.top) -
                Math.hypot(secondPosition.left - origin.left, secondPosition.top - origin.top)
            );
          })[0];
        if (next) setHideout({ element: next, side: "top", offset: 24 });
        else scheduleSneak();
      }, 4000);
    };
    const conceal = () => {
      setConcealed(true);
      coverTimer.current = window.setTimeout(() => {
        movement.current?.cancel();
        setConcealed(false);
        if (traveler.current && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          movement.current = animatePeek(traveler.current, hideout.side);
          movement.current.onfinish = scheduleSneak;
        }
      }, 15_000);
    };
    const arrive = () => {
      if (entrance.current) {
        entrance.current = false;
        scheduleSneak();
      } else if (hideout.concealOnArrival) {
        const element = traveler.current;
        if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          conceal();
          return;
        }
        const clipPath = getComputedStyle(element).clipPath;
        movement.current?.cancel();
        movement.current = element.animate(
          [{ transform: "translate(0, 0)", clipPath }, getConcealedPose(hideout.side)],
          { duration: 500, easing: "ease-in", fill: "forwards" }
        );
        movement.current.onfinish = conceal;
      } else {
        scheduleSneak();
      }
    };
    const follow = () => {
      if (!hideout.element.isConnected) {
        window.clearTimeout(entranceTimer.current);
        entrance.current = false;
        setIdle(false);
        setConcealed(false);
        setHideout(null);
        setPosition(null);
        return;
      }
      const target = getHideoutPosition(hideout);
      if (lastPosition?.left === target.left && lastPosition.top === target.top) return;
      const arriving = lastPosition === null;
      lastPosition = target;
      const element = traveler.current;
      const start = element?.getBoundingClientRect();
      movement.current?.cancel();
      setPosition(target);
      if (
        arriving &&
        element &&
        start &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        movement.current = entrance.current
          ? animatePeek(element, hideout.side)
          : element.animate(
              [
                {
                  transform: `translate(${start.left - target.left}px, ${start.top - target.top}px)`,
                  clipPath: "inset(-8px)",
                },
                { offset: 0.8, transform: "translate(0, 0)", clipPath: "inset(-8px)" },
                {
                  transform: "translate(0, 0)",
                  clipPath: getComputedStyle(element).clipPath,
                },
              ],
              {
                duration: Math.max(
                  1200,
                  Math.min(2400, Math.hypot(start.left - target.left, start.top - target.top) * 4)
                ),
                easing: "ease-in-out",
                fill: "forwards",
              }
            );
        movement.current.onfinish = arrive;
      } else if (arriving) {
        arrive();
      }
    };
    follow();
    const observer = new ResizeObserver(follow);
    observer.observe(hideout.element);
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    return () => {
      movement.current?.cancel();
      window.clearTimeout(coverTimer.current);
      window.clearTimeout(entranceTimer.current);
      observer.disconnect();
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [hideout, idle, hidden, menuOpen, pickedUp, pendingHideout]);

  useEffect(() => {
    if (pickedUp) placementArea.current?.focus({ preventScroll: true });
  }, [pickedUp]);

  const moveTo = (left: number, top: number) => {
    setPosition({
      left: Math.max(0, Math.min(left, window.innerWidth - 64)),
      top: Math.max(0, Math.min(top, window.innerHeight - 64)),
    });
  };

  const pickUp = () => {
    const bounds = traveler.current?.getBoundingClientRect();
    if (!bounds) return;
    pickupOrigin.current = position;
    previousHideout.current = hideout;
    window.clearTimeout(coverTimer.current);
    setConcealed(false);
    setPendingHideout(null);
    setHideout(null);
    moveTo(bounds.left, bounds.top);
    setPickedUp(true);
  };

  const putDown = (cancel = false, droppedAt = position) => {
    setPendingHideout(null);
    if (cancel) {
      setPosition(pickupOrigin.current);
      setHideout(
        previousHideout.current ? { ...previousHideout.current, concealOnArrival: false } : null
      );
    } else if (droppedAt) {
      const center = { left: droppedAt.left + 32, top: droppedAt.top + 32 };
      let nearest: Hideout | null = null;
      let distance = 64;
      let containingArea = Infinity;
      getHideoutElements().forEach(element => {
        const bounds = element.getBoundingClientRect();
        const containsDrop =
          center.left >= bounds.left &&
          center.left <= bounds.right &&
          center.top >= bounds.top &&
          center.top <= bounds.bottom;
        const area = bounds.width * bounds.height;
        const bottomIsClosest =
          Math.abs(center.top - bounds.bottom) <
          Math.min(
            Math.abs(center.left - bounds.left),
            Math.abs(center.left - bounds.right),
            Math.abs(center.top - bounds.top)
          );
        for (const side of ["left", "right", "top"] as const) {
          if (bottomIsClosest && side === "top") continue;
          const isVerticalEdge = side === "left" || side === "right";
          const edge = bounds[side];
          const viewportSize = isVerticalEdge ? window.innerWidth : window.innerHeight;
          if (edge < 40 || edge > viewportSize - 40) continue;
          const perpendicular = isVerticalEdge
            ? Math.max(bounds.top - center.top, 0, center.top - bounds.bottom)
            : Math.max(bounds.left - center.left, 0, center.left - bounds.right);
          const candidate = Math.hypot(
            (isVerticalEdge ? center.left : center.top) - edge,
            perpendicular
          );
          if (
            containsDrop
              ? area < containingArea || (area === containingArea && candidate < distance)
              : containingArea === Infinity && candidate < distance
          ) {
            if (containsDrop) containingArea = area;
            distance = candidate;
            nearest = {
              element,
              side,
              concealOnArrival: true,
              offset: Math.max(
                0,
                isVerticalEdge ? center.top - bounds.top - 32 : center.left - bounds.left - 32
              ),
            };
          }
        }
      });
      setPendingHideout(nearest);
    }
    setPickedUp(false);
    traveler.current?.querySelector("button")?.focus({ preventScroll: true });
  };

  if (hidden || !idle) return null;

  return (
    <aside
      ref={companion}
      className={[S.ninja, menuOpen ? S.paused : "", pickedUp ? S.pickedUp : ""].join(" ")}
      aria-label="Ninja companion"
    >
      {pickedUp && (
        <button
          ref={placementArea}
          type="button"
          className={S.placementArea}
          aria-label="Place ninja. Use arrow keys to move, Enter to place, or Escape to cancel."
          onPointerMove={event => moveTo(event.clientX - 32, event.clientY - 32)}
          onClick={event => {
            const droppedAt =
              event.detail > 0
                ? {
                    left: Math.max(0, Math.min(event.clientX - 32, window.innerWidth - 64)),
                    top: Math.max(0, Math.min(event.clientY - 32, window.innerHeight - 64)),
                  }
                : position;
            if (droppedAt) setPosition(droppedAt);
            putDown(false, droppedAt);
          }}
          onKeyDown={event => {
            const offsets: Record<string, [number, number]> = {
              ArrowLeft: [-16, 0],
              ArrowRight: [16, 0],
              ArrowUp: [0, -16],
              ArrowDown: [0, 16],
            };
            const offset = offsets[event.key];
            if (offset && position) {
              event.preventDefault();
              moveTo(position.left + offset[0], position.top + offset[1]);
            } else if (event.key === "Escape" || event.key === "Tab") {
              event.preventDefault();
              putDown(true);
            }
          }}
        />
      )}
      <div
        ref={traveler}
        className={[
          S.traveler,
          position ? S.placed : "",
          hideout ? S[hideout.side] : "",
          concealed ? S.concealed : "",
        ].join(" ")}
        style={
          position
            ? {
                left: hideout ? position.left : `min(${position.left}px, calc(100vw - 64px))`,
                top: hideout ? position.top : `min(${position.top}px, calc(100dvh - 64px))`,
              }
            : undefined
        }
      >
        <Button
          className={S.characterButton}
          unstyled
          aria-label="Ninja actions"
          aria-haspopup="menu"
          aria-controls={menuId}
          aria-expanded={menuOpen}
          title="Ninja actions"
          onClick={event => {
            window.clearTimeout(entranceTimer.current);
            window.clearTimeout(coverTimer.current);
            entrance.current = false;
            movement.current?.cancel();
            menu.current?.toggle(event);
          }}
        >
          <img className={S.character} src={ninja} alt="" width="64" height="64" />
        </Button>
      </div>
      <Menu
        ref={menu}
        id={menuId}
        popup
        style={{ pointerEvents: menuOpen ? "auto" : "none" }}
        model={[
          { label: "Pick up", icon: "pi pi-arrows-alt", command: pickUp },
          { label: "Dismiss", icon: "pi pi-times", command: onDismiss },
        ]}
        onShow={() => setMenuOpen(true)}
        onHide={() => setMenuOpen(false)}
      />
    </aside>
  );
}
